import { useState, type ReactNode } from 'react';
import {
  pixelCitizenCatalog as cat, pcChoiceSelections, pcSwatches,
  type PcCustomization, type PcLayerId, type PcSlot,
} from '@campus/core';
import { Button, Tabs, cx } from '../ds/index.js';
import { PcPreview } from './PcPreview.js';
import { randomPc, withDefaultColors } from './pcAppearance.js';

type BioKey = 'skin' | 'hair' | 'facial-hair' | 'eyes';
type Section =
  | { kind: 'layer'; layer: PcLayerId; label: string }
  | { kind: 'bio'; bio: BioKey; label: string };

type TabId = 'body' | 'face' | 'hair' | 'outfit';

const TABS: Array<{ id: TabId; label: string; sections: Section[] }> = [
  { id: 'body', label: 'Corps', sections: [
    { kind: 'layer', layer: 'body', label: 'Silhouette' },
    { kind: 'bio', bio: 'skin', label: 'Peau' },
    { kind: 'layer', layer: 'head', label: 'Forme du visage' },
    { kind: 'layer', layer: 'nose', label: 'Nez' },
  ] },
  { id: 'face', label: 'Visage', sections: [
    { kind: 'layer', layer: 'eyes', label: 'Yeux' },
    { kind: 'bio', bio: 'eyes', label: 'Couleur des yeux' },
    { kind: 'layer', layer: 'face-accessory', label: 'Lunettes' },
    { kind: 'layer', layer: 'facial-hair', label: 'Barbe' },
    { kind: 'bio', bio: 'facial-hair', label: 'Couleur de la barbe' },
  ] },
  { id: 'hair', label: 'Cheveux', sections: [
    { kind: 'layer', layer: 'hair-front', label: 'Coiffure' },
    { kind: 'layer', layer: 'hair-back', label: 'Cheveux longs' },
    { kind: 'bio', bio: 'hair', label: 'Couleur des cheveux' },
    { kind: 'layer', layer: 'headwear', label: 'Couvre-chef' },
  ] },
  { id: 'outfit', label: 'Tenue', sections: [
    { kind: 'layer', layer: 'top', label: 'Haut' },
    { kind: 'layer', layer: 'outerwear', label: 'Veste' },
    { kind: 'layer', layer: 'bottom', label: 'Bas' },
    { kind: 'layer', layer: 'footwear', label: 'Chaussures' },
  ] },
];

/** Change d'objet en gardant ses couleurs quand elles restent valables. */
function switchChoice(layer: PcLayerId, current: PcSlot, choice: string | null): PcSlot {
  if (choice === null) return '%none%';
  const next = withDefaultColors(layer, choice);
  if (current === '%none%' || next === '%none%') return next;
  const specs = pcChoiceSelections(layer, choice);
  const colors = specs.map((spec, i) => {
    const kept = current.colors[i];
    return kept && pcSwatches(spec).includes(kept) ? kept : next.colors[i]!;
  });
  return { choice, colors };
}

export function Swatches({ label, colors, value, onPick }: {
  label: string; colors: string[]; value: string | undefined; onPick: (c: string) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '2px 0 2px 2px' }}>
      {colors.map((c, i) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={value === c}
          aria-label={`${label} ${i + 1}`}
          className={cx('pl-swatch', value === c && 'pl-swatch-on')}
          style={{ background: c }}
          onClick={() => onPick(c)}
        />
      ))}
    </div>
  );
}

/**
 * Éditeur d'avatar Pixel Citizen (contrôlé) : grand aperçu à gauche (avec
 * `aside` dessous, ex. le nom affiché), onglets Corps · Visage · Cheveux ·
 * Tenue à droite. Les boutons Annuler/Enregistrer appartiennent à l'écran qui
 * l'accueille. Sur mobile (`compact`), l'aperçu passe au-dessus.
 */
export function PcAvatarEditor({ value, onChange, headers, aside, compact = false }: {
  value: PcCustomization;
  onChange: (pc: PcCustomization) => void;
  /** En-têtes d'authentification pour l'aperçu (POST /avatars/preview). */
  headers: Record<string, string>;
  aside?: ReactNode;
  compact?: boolean;
}) {
  const [tab, setTab] = useState<TabId>('body');
  const current = TABS.find((t) => t.id === tab)!;

  const setLayer = (layer: PcLayerId, slot: PcSlot) => onChange({ ...value, [layer]: slot });
  const hair = value['hair-color'];

  const renderSection = (s: Section) => {
    if (s.kind === 'bio') {
      if (s.bio === 'facial-hair' && value['facial-hair'] === '%none%') return null;
      const [colors, selected, pick] = {
        skin: [cat.bio.skin, value['skin-color'][0], (c: string) => onChange({ ...value, 'skin-color': [c] })],
        // Cheveux : cuir chevelu, barbe et sourcils ensemble ; la barbe se change ensuite à part
        hair: [cat.bio.hair, hair[0], (c: string) => onChange({ ...value, 'hair-color': [c, c, c] })],
        'facial-hair': [cat.bio.hair, hair[1], (c: string) => onChange({ ...value, 'hair-color': [hair[0], c, hair[2]] })],
        eyes: [cat.bio.eyes, value['eye-color'][0], (c: string) => onChange({ ...value, 'eye-color': [c] })],
      }[s.bio] as [string[], string, (c: string) => void];
      return (
        <section key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="pl-field-label">{s.label}</span>
          <Swatches label={s.label} colors={colors} value={selected} onPick={pick} />
        </section>
      );
    }

    const def = cat.layers[s.layer];
    const slot = value[s.layer];
    const selected = slot === '%none%' ? null : slot.choice;
    const options: Array<{ id: string | null; label: string }> = [
      ...(def.optional ? [{ id: null, label: 'Aucun' }] : []),
      ...def.choices.filter((c) => !c.hidden || c.id === selected).map((c) => ({ id: c.id, label: c.label })),
    ];
    const specs = selected ? pcChoiceSelections(s.layer, selected) : [];
    return (
      <section key={s.label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span className="pl-field-label">{s.label}</span>
        <div role="radiogroup" aria-label={s.label} className="pl-choices">
          {options.map((o) => (
            <button
              key={o.id ?? 'none'}
              type="button"
              role="radio"
              aria-checked={selected === o.id}
              className="pl-choice"
              onClick={() => setLayer(s.layer, switchChoice(s.layer, slot, o.id))}
            >
              {o.label}
            </button>
          ))}
        </div>
        {slot !== '%none%' && specs.map((spec, i) => (
          <Swatches
            key={i}
            label={specs.length > 1 ? `${s.label} - ${spec.label.toLowerCase()}` : `Couleur - ${s.label.toLowerCase()}`}
            colors={pcSwatches(spec)}
            value={slot.colors[i]}
            onPick={(c) => setLayer(s.layer, { ...slot, colors: slot.colors.map((old, j) => (j === i ? c : old)) })}
          />
        ))}
      </section>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: compact ? 'column' : 'row', gap: compact ? 16 : 24, flex: 1, minHeight: 0, color: 'var(--color-text-primary)' }}>
      <section aria-label="Aperçu" style={compact
        ? { display: 'flex', alignItems: 'center', gap: 16 }
        : { width: 184, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          className="pl-page"
          style={{
            width: compact ? 112 : '100%', flexShrink: 0, boxSizing: 'border-box', padding: compact ? '12px 0 4px' : '20px 0 8px',
            borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border-default)',
            display: 'flex', justifyContent: 'center',
          }}
        >
          <PcPreview draft={{ v: 2, pc: value }} headers={headers} height={compact ? 84 : 168} />
        </div>
        <div style={{ flex: compact ? 1 : undefined, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {aside}
          <Button icon="shuffle" onClick={() => onChange(randomPc())}>Au hasard</Button>
        </div>
      </section>

      <section aria-label="Personnaliser l'avatar" style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <Tabs aria-label="Partie de l'avatar" items={TABS} value={tab} onChange={setTab} />
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 20,
          // Hauteur : la place qui reste (parent de hauteur limitée), au plus 420 px sur un grand écran
          ...(compact ? {} : { flex: '1 1 auto', minHeight: 160, maxHeight: 420, overflowY: 'auto', paddingRight: 4 }),
        }}>
          {current.sections.map(renderSection)}
        </div>
      </section>
    </div>
  );
}
